import { format } from "date-fns";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";

import CheckIcon from "src/core/assets/images/icons/checkIcon.svg?react";
import CloseIcon from "src/core/assets/images/icons/closeCommentIcon.svg?react";
import EditIcon from "src/core/assets/images/icons/edit.svg?react";
import MessagetThreadIcon from "src/core/assets/images/icons/messageThread.svg?react";
import { FormTextarea } from "src/core/components/custom-ui/form-textarea";
import { Spinner } from "src/core/components/custom-ui/spinner";
import { Button } from "src/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "src/core/components/ui/dialog";
import { useFolderMessageThreadModal } from "src/core/hooks/folder/useFolderMessageThreadModal";
import { cn } from "src/core/lib/utils";

export const FolderMessageThreadModal = ({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const { t } = useTranslation();
  const {
    form,
    onSubmit,
    messageThread,
    isLoading,
    user,
    isEditable,
    lastUserMessageId,
    handleEditMessage,
    editingMessageId,
    formEditing,
    onSubmitEditing,
    isLoadingEditing,
  } = useFolderMessageThreadModal();

  return (
    <Dialog open={open} onOpenChange={setOpen} variant="right-panel">
      <DialogTitle></DialogTitle>
      <DialogDescription></DialogDescription>
      <DialogContent className="messageThread-modal" hideCloseButton>
        <Button variant="sideTab" size="none" onClick={() => setOpen(false)}>
          <div className="flex items-center justify-center w-30 h-30 rounded-full bg-muted group-hover:text-foreground text-white">
            <MessagetThreadIcon />
          </div>
          <span className="text-muted-foreground group-hover:text-foreground group-hover:underline">
            {t("messageThread.button.close")}
          </span>
        </Button>

        {isEditable && (
          <>
            <FormProvider {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <FormTextarea
                  name="content"
                  label={t("messageThread.label")}
                  control={form.control}
                  className="h-[60px] mt-10 bg-dialog"
                  required
                />
                <div className="flex flex-row items-center justify-end mt-10 ">
                  <Button type="submit" variant="default" loading={isLoading}>
                    <MessagetThreadIcon />
                    {t("messageThread.button.send")}
                  </Button>
                </div>
              </form>
            </FormProvider>
            <div className="w-full h-[1px] bg-border-secondary mt-10 mb-20 font-light ">&nbsp;</div>
            <div className="flex flex-col mb-20">
              <p className="font-medium">{t("messageThread.title")}</p>
              <p className="font-light text-muted-foreground">{t("messageThread.subtitle")}</p>
            </div>
          </>
        )}

        {messageThread?.messages.length === 0 ? (
          <div className="text-muted-foreground flex h-label items-center">
            {t("messageThread.noComments")}
          </div>
        ) : (
          <div className="flex flex-col gap-y-20 h-[calc(100vh-360px)] overflow-y-auto">
            {messageThread?.messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  "folder-messageThread-wrapper",
                  message?.adminStatus?.author?.id === user?.id ? "self-end" : "self-start"
                )}
              >
                {editingMessageId === message.id && isEditable ? (
                  <FormProvider {...formEditing}>
                    <form onSubmit={formEditing.handleSubmit(onSubmitEditing)}>
                      <div className="folder-messageThread bg-dialog flex-col">
                        <FormTextarea
                          name="content"
                          control={formEditing.control}
                          className="border-none w-full bg-dialog resize-none p-0"
                          wrapperClassName="w-full"
                          required
                        />
                        <div className="w-full h-[1px] bg-border-secondary my-10 font-light ">
                          &nbsp;
                        </div>
                        <div className="flex flex-row items-center justify-end gap-x-20 text-muted-foreground">
                          {isLoadingEditing ? (
                            <Spinner size="sm" />
                          ) : (
                            <>
                              <CloseIcon
                                className="cursor-pointer"
                                onClick={() => handleEditMessage(null)}
                              />
                              <CheckIcon
                                className="cursor-pointer"
                                onClick={() => formEditing.handleSubmit(onSubmitEditing)()}
                              />
                            </>
                          )}
                        </div>
                      </div>
                    </form>
                  </FormProvider>
                ) : (
                  <div
                    className={cn(
                      "folder-messageThread relative group",
                      message?.adminStatus?.author?.id === user?.id
                        ? "self-messageThread"
                        : "other-messageThread"
                    )}
                  >
                    {message.content}
                    {lastUserMessageId === message.id && isEditable && (
                      <Button
                        variant="iconAction"
                        size="none"
                        className="absolute hidden self-center right-10 w-[45px] h-40 group-hover:flex"
                        onClick={() => handleEditMessage(message)}
                      >
                        <EditIcon />
                      </Button>
                    )}
                  </div>
                )}
                <div className="folder-messageThread-author">
                  {t("messageThread.author", {
                    author:
                      message?.adminStatus?.author?.firstName +
                      " " +
                      message?.adminStatus?.author?.lastName.toUpperCase(),
                    date: format(new Date(message?.adminStatus?.lastModification), "dd/MM/yyyy"),
                  })}
                </div>
                {message?.adminStatus?.lastModification !== message?.adminStatus?.creation && (
                  <div className="folder-messageThread-updated">
                    {t("messageThread.updatedAt", {
                      date: format(new Date(message?.adminStatus?.lastModification), "dd/MM/yyyy"),
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
